import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FolderOpen, Sparkles, Users, Calendar, Tag, Shield, Zap, Search } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function Features({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none reverse' } }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  const features = [
    { icon: FolderOpen, title: 'Asset Library', description: 'Centralized storage for all logos, headshots, backgrounds, and brand elements.' },
    { icon: Users, title: 'Entity Profiles', description: 'Every artist and venue has a profile with their associated assets.' },
    { icon: Sparkles, title: 'AI Flyer Generation', description: 'Generate professional flyers using your actual assets, not random images.' },
    { icon: Calendar, title: 'Event Management', description: 'Organize events with dates, venues, lineups, and linked assets.' },
    { icon: Tag, title: 'Smart Tagging', description: 'Auto-tag assets by entity type—artists, venues, sponsors.' },
    { icon: Search, title: 'Instant Search', description: 'Find any asset in seconds with powerful search and filters.' },
    { icon: Zap, title: 'One-Click Generate', description: 'Select an event, click a button—flyer ready in seconds.' },
    { icon: Shield, title: 'Brand Consistency', description: 'AI uses your brand colors, fonts, and visual identity every time.' },
  ];

  return (
    <section ref={sectionRef} id="features" className={`relative bg-[#0B0F10] py-14 ${className}`}>
      <div className="w-full px-6 lg:px-12">
        <div ref={contentRef} className="max-w-6xl mx-auto" style={{ willChange: 'transform, opacity' }}>
          <div className="text-center mb-10">
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#2EC4B6] mb-4 block">Features</span>
            <h2 className="font-display font-bold text-[clamp(36px,4.5vw,64px)] leading-tight text-white mb-4">Everything you need.</h2>
            <p className="text-lg text-[#A9B4B8] max-w-2xl mx-auto">
              A complete system for managing event assets and generating flyers with AI.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, i) => (
              <div key={i} className="bg-[#1a1f21] rounded-xl p-5 border border-white/10 hover:border-[#2EC4B6]/30 transition-all group">
                <div className="w-12 h-12 rounded-lg bg-[#2EC4B6]/10 flex items-center justify-center mb-4 group-hover:bg-[#2EC4B6]/20 transition-colors">
                  <feature.icon size={24} className="text-[#2EC4B6]" />
                </div>
                <h3 className="font-display font-semibold text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-[#A9B4B8] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
